import type { Locale } from '@jws/shared';

export type MediaItem = { id: number; role: string; url: string; alt: string };
export type Product = {
  id: number;
  slug: string;
  sku: string | null;
  name: string;
  summary: string | null;
  descriptionHtml: string | null;
  features: string[];
  applications: string[];
  attributes: {
    brand: string | null;
    certification: string | null;
    origin: string | null;
    custom: Array<{ name: string; value: string; sortOrder: number }>;
  };
  trade: {
    moq: string | null;
    price: string | null;
    paymentMethod: string | null;
    supplyAbility: string | null;
    deliveryTime: string | null;
    packaging: string | null;
  };
  category: { id: number; slug: string; name: string };
  media: MediaItem[];
  specs: Array<{ groupName: string | null; label: string; value: string }>;
  attachments: Array<{ title: string; url: string }>;
  videos: Array<{ id: number; title: string; description: string | null; playbackUrl: string | null; posterUrl: string | null; durationSec: number | null }>;
  seo: { title: string; description: string | null; keywords: string | null };
};
export type Category = { id: number; slug: string; name: string; description?: string | null; productCount?: number };
export type Article = {
  id: number;
  type: 'news' | 'case';
  slug: string;
  title: string;
  excerpt: string | null;
  contentHtml?: string | null;
  coverUrl: string | null;
  publishedAt: string | null;
  seo: { title: string; description: string | null };
};
export type Video = {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  playbackUrl: string | null;
  posterUrl: string | null;
  durationSec: number | null;
};
export type ContentPage = {
  slug: string;
  title: string;
  summary: string | null;
  contentHtml: string | null;
  seo: { title: string; description: string | null };
};
export type FactoryAlbum = {
  id: number;
  slug: string;
  title: string;
  images: Array<{ id: number; url: string; alt: string; caption: string | null }>;
};
export type Contact = {
  companyName: string;
  address: string | null;
  introHtml: string | null;
  channels: Array<{ type: string; label: string; value: string; href: string | null; isPrimary: boolean }>;
};
export type Bootstrap = {
  locale: Locale;
  categories: Category[];
  settings: Record<string, unknown>;
  contact: Contact | null;
};

type Envelope<T> = { data: T; meta?: { page: number; pageSize: number; total: number } };
// 调用点均已包含 /api/...。部署变量表示站点前缀而非 API 路径；
// 同时兼容旧的 /_preview/api 配置，防止出现 /api/api 的双重路径。
const apiBase = (import.meta.env.VITE_API_BASE_URL || '')
  .replace(/\/api\/?$/, '')
  .replace(/\/$/, '');

function apiUrl(path: string): string {
  return `${apiBase}${path}`;
}

export async function getBootstrap(locale: Locale): Promise<Bootstrap> {
  return (await request<Envelope<Bootstrap>>(`/api/public/bootstrap?lang=${locale}`)).data;
}

export async function getProducts(locale: Locale, params: Record<string, string> = {}): Promise<Envelope<Product[]>> {
  const query = new URLSearchParams({ lang: locale, ...params });
  return request<Envelope<Product[]>>(`/api/public/products?${query}`);
}

export async function getProduct(locale: Locale, slug: string): Promise<Product> {
  return (await request<Envelope<Product>>(`/api/public/products/${encodeURIComponent(slug)}?lang=${locale}`)).data;
}

export async function getArticles(locale: Locale, type: 'news' | 'case'): Promise<Envelope<Article[]>> {
  return request<Envelope<Article[]>>(`/api/public/articles?lang=${locale}&type=${type}`);
}

export async function getArticle(locale: Locale, type: 'news' | 'case', slug: string): Promise<Article> {
  return (await request<Envelope<Article>>(`/api/public/articles/${type}/${encodeURIComponent(slug)}?lang=${locale}`)).data;
}

export async function getVideos(locale: Locale, limit = 12): Promise<Video[]> {
  const query = new URLSearchParams({ lang: locale, limit: String(limit) });
  return (await request<Envelope<Video[]>>(`/api/public/videos?${query}`)).data;
}

export async function getFactoryAlbums(locale: Locale): Promise<FactoryAlbum[]> {
  return (await request<Envelope<FactoryAlbum[]>>(`/api/public/factory-albums?lang=${locale}`)).data;
}

export async function getPage(locale: Locale, slug: string): Promise<ContentPage> {
  return (await request<Envelope<ContentPage>>(
    `/api/public/pages/${encodeURIComponent(slug)}?lang=${locale}`
  )).data;
}

export async function submitInquiry(input: {
  productId?: number | null;
  name: string;
  company?: string | null;
  email: string;
  phone?: string | null;
  country?: string | null;
  message: string;
  locale: Locale;
  sourcePath: string;
}) {
  return request<Envelope<{ id?: number; accepted: boolean }>>('/api/public/inquiries', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  });
}

export function recordVisit(input: { path: string; referrer: string | null; locale: Locale }): void {
  void fetch(apiUrl('/api/public/visits'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
    keepalive: true
  }).catch(() => undefined);
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  // 公开内容由后台实时维护；避免浏览器复用旧的 bootstrap/分类响应，
  // 让新增分类、排序和发布状态在刷新后立即生效。
  const response = await fetch(apiUrl(url), { cache: 'no-store', ...init });
  const body = await response.json() as T & { error?: { message?: string } };
  if (!response.ok) throw new Error(body.error?.message || `请求失败（${response.status}）`);
  return body;
}
